/**
 * Cross-tab cache of the sidebar's shared-thread participant counts
 * (2026-08-08 tab-parity fix).
 *
 * `sharedThreadParticipants` — the map that decides which sidebar rows land in
 * the "group threads" section and what the header's "N people" line says — was
 * per-tab React state hydrated only by each tab's OWN point-in-time
 * `threadMembershipRead` fetch. Two tabs of the same account routinely
 * disagreed: the tab whose fetch succeeded rendered "group threads · 8 /
 * threads · 7" while a sibling whose fetch failed (or hadn't run) rendered one
 * flat "threads · 15" section.
 *
 * This module persists the LAST SUCCESSFUL read per (instance, uid) so that:
 *   • a fresh tab paints the correct sections immediately (stale-while-
 *     revalidate — its own fetch still runs and overwrites);
 *   • a `storage` event carries every successful read to all sibling tabs,
 *     converging the section layout account-wide in real time.
 *
 * PRESENTATION ONLY: these counts drive sidebar grouping and labels — never
 * authorization. `canPost` / server gates keep their own fail-closed reads.
 * Pure functions over an injected Storage so this is trivially unit-testable.
 */

export const SHARED_PARTICIPANTS_CACHE_VERSION = 1;

/** localStorage key for one (instance, uid) pair's participant-count map. */
export function sharedParticipantsCacheKey(instanceId: string, uid: string): string {
  return `prest0_shared_participants__${instanceId}__${uid}`;
}

interface SharedParticipantsCachePayload {
  v: number;
  at: number;
  counts: Record<string, number>;
}

/**
 * One cached read. `at` is the read's FETCH-START time (not its write time):
 * two tabs' reads must be ordered by when they asked the server, or a slow
 * stale response resolving after a sibling's fresher read would win
 * last-write-wins and re-poison every tab (e.g. re-showing a group thread the
 * user was just removed from). Consumers adopt a payload only when its `at`
 * is >= the stamp of the map they already applied.
 */
export interface SharedParticipantsCacheSnapshot {
  at: number;
  counts: Record<string, number>;
}

/** Parse + validate a raw cache payload; null on anything malformed. */
export function parseSharedParticipantsCache(
  raw: string | null,
): SharedParticipantsCacheSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SharedParticipantsCachePayload> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.v !== SHARED_PARTICIPANTS_CACHE_VERSION) return null;
    const counts = parsed.counts;
    if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return null;
    const out: Record<string, number> = {};
    for (const [threadId, n] of Object.entries(counts)) {
      const id = (threadId ?? '').trim();
      if (!id) continue;
      const count = Number(n);
      if (!Number.isFinite(count) || count <= 0) continue;
      out[id] = Math.floor(count);
    }
    const at = Number(parsed.at);
    return { at: Number.isFinite(at) && at > 0 ? at : 0, counts: out };
  } catch {
    return null;
  }
}

/** Last successful read for (instance, uid); null when absent/malformed. */
export function loadSharedParticipantsCache(
  storage: Pick<Storage, 'getItem'> | null,
  instanceId: string,
  uid: string,
): SharedParticipantsCacheSnapshot | null {
  if (!storage || !instanceId || !uid) return null;
  try {
    return parseSharedParticipantsCache(storage.getItem(sharedParticipantsCacheKey(instanceId, uid)));
  } catch {
    return null;
  }
}

/**
 * Persist a successful read's counts (an empty map is a REAL answer — "no
 * group threads" — and is persisted too, so sibling tabs converge on it).
 * `at` must be the read's fetch-start time (see SharedParticipantsCacheSnapshot).
 * Best-effort: quota/privacy-mode failures are swallowed.
 */
export function saveSharedParticipantsCache(
  storage: Pick<Storage, 'setItem'> | null,
  instanceId: string,
  uid: string,
  counts: Record<string, number>,
  at: number,
): void {
  if (!storage || !instanceId || !uid) return;
  try {
    const payload: SharedParticipantsCachePayload = {
      v: SHARED_PARTICIPANTS_CACHE_VERSION,
      at: Number.isFinite(at) && at > 0 ? at : Date.now(),
      counts,
    };
    storage.setItem(sharedParticipantsCacheKey(instanceId, uid), JSON.stringify(payload));
  } catch {
    // Best-effort presentation cache — never let it break the read path.
  }
}
